/**
 * Eigenbau: Band der offenen Merge Requests auf „Heute“.
 *
 * Ein offener Merge Request wartet auf einen Menschen, und bis hierher stand
 * dieser Hinweis nur an einer Stelle: als stille Zahl am Punkt „Lieferung“ der
 * Schiene — und den gibt es erst, wenn genau ein Projekt gewählt ist. Wer mit
 * „Alle Projekte“ arbeitete, sah nirgends, dass irgendwo etwas liegt.
 *
 * Kein Tremor-Gegenstück: Die Blocksammlung hat Karten und Kennzahlen, aber
 * dieses Band ist eine Rubrik über Zeilen — dieselbe Form, in der „Heute“
 * seine Listen führt. Eine Karte daneben wäre eine zweite Sprache auf einer
 * Seite, die schon eine hat.
 *
 * Die Zahl trägt den Akzent, der Name nicht: Der Entwurf hält den Akzent für
 * das frei, was eine Handlung verlangt. Bei keinem offenen Merge Request
 * erscheint das Band gar nicht — eine Null in Akzentfarbe behauptete Alarm,
 * wo Ruhe ist.
 */

import { Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { Mikrolabel } from "@/components/entwurf/Mikrolabel";
import { Rubrik } from "@/components/entwurf/Rubrik";
import { Kennung, Zeilentitel, zeileKlassen } from "@/components/entwurf/Zeile";
import { schluessel } from "@/lib/abfragen";
import { api } from "@/lib/api";
import { cx } from "@/lib/utils";

export function OffeneMrBand({ className }: { className?: string }) {
  const offene = useQuery({
    queryKey: schluessel.gitlabOffeneMrs(),
    queryFn: () => api.gitlab.offeneJeProjekt.query(),
  });

  /*
   * Ein Fehlschlag bleibt sichtbar, aber leise. Nichts anzuzeigen wäre
   * dasselbe Bild wie „keine offenen Merge Requests“ — und das ist die eine
   * Aussage, die dieses Band nicht fälschlich machen darf.
   */
  if (offene.isError) {
    return (
      <Mikrolabel grad={11} className={cx("block", className)}>
        Offene Merge Requests konnten nicht geladen werden.
      </Mikrolabel>
    );
  }

  const projekte = offene.data ?? [];
  if (projekte.length === 0) return null;

  const gesamt = projekte.reduce((summe, projekt) => summe + projekt.anzahl, 0);

  /*
   * Das Ziel ist eine gewöhnliche Zeichenkette, wie in der Schiene: Die
   * Reiter der Projektseite laufen heute über `?reiter=` und nicht über
   * eigene Routen, und der Routentyp kennt diese Adressen deshalb nicht.
   */
  const lieferungsZiel = (projektId: string): string =>
    `/projekte/${projektId}?reiter=lieferung`;

  return (
    <section className={className} aria-labelledby="rubrik-offene-mr">
      <Rubrik id="rubrik-offene-mr" anzahl={gesamt === 1 ? "1 offen" : `${gesamt} offen`}>
        Merge Requests
      </Rubrik>
      <ul aria-labelledby="rubrik-offene-mr" data-testid="offene-mr-band">
        {projekte.map((projekt) => (
          <li key={projekt.projektId}>
            <Link
              to={lieferungsZiel(projekt.projektId)}
              className={zeileKlassen({ hoehe: 44, klickbar: true })}
            >
              <Kennung breite={34}>{projekt.kuerzel}</Kennung>
              <Zeilentitel titel={projekt.name} leise>
                {projekt.name}
              </Zeilentitel>
              <Mikrolabel grad={11} zahl className="flex-none text-akzent">
                {projekt.anzahl}
              </Mikrolabel>
            </Link>
          </li>
        ))}
      </ul>
    </section>
  );
}
